import { useForm } from "react-hook-form";
import { CBInput, CBSelect } from "../../shared";
import { CBHandMessage } from "../../shared/CBHandMessage/CBHandMessage";
import CustomCard from "../../shared/CustomCard/CustomCard";
import { yupResolver } from "@hookform/resolvers/yup";
import { profileObj, userProfileSchema, userProfileValues } from "../utils";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../context/user.context";
import { Button, Radio, RadioChangeEvent, Typography } from "antd";
import { designationsOptions } from "../../../constants/designations.constants";
import {
  CitiesOpts,
  CountriesOpts,
  StatesOpts,
} from "../../../constants/countries.constants";
import { CheckOutlined, UserOutlined } from "@ant-design/icons";
import User from "../../../interfaces/user.interface";
import {
  getUserDetails,
  updateUser as updateUserDto,
} from "../../../services/users.service";
import { useNavigate } from "react-router-dom";
import { CheckboxGroupProps } from "antd/es/checkbox";
import Message from "./../../shared/Message/Message";

interface IProfile {
  userId?: string;
}
interface ListsI {
  value: string;
  label: string;
  code: string;
}

const Profile = ({ userId }: IProfile) => {
  const navigate = useNavigate();
  const [country, setCountry] = useState<string>("0");
  const [countryCode, setCountryCode] = useState<ListsI>();
  const [userFound, setUserFound] = useState<User | undefined>();
  const [state, setState] = useState<string>("");
  const { user, updateUser } = useContext(UserContext);
  const { Text } = Typography;
  const countries: ListsI[] = CountriesOpts();
  const [states, setStates] = useState<ListsI[]>([]);
  const [cities, setCities] = useState<ListsI[]>([]);
  const { control, watch, handleSubmit, setValue } = useForm({
    mode: "onBlur",
    resolver: yupResolver(userProfileSchema),
    values: userProfileValues(userId ? userFound! : user!),
    defaultValues: { title: "mr" },
  });

  const onChangeTitle = (e: RadioChangeEvent) =>
    setValue("title", e.target.value, { shouldDirty: true });

  const changeCountry = (val: string) => {
    const element = countries.find((item) => {
      return item.value === val;
    });
    setCountryCode(element);
    setCountry(element?.code ?? "");

    const newStatesOps = StatesOpts(element?.code ?? "");
    setStates(newStatesOps);
    setState("");

    setCities([]);

    setValue("province", newStatesOps.length ? newStatesOps[0].value : "");
    setValue("city", "");
  };

  const changeState = (val: string) => {
    const element = states.find((item) => {
      return item.value === val;
    });
    setState(element?.code ?? "");
    setCities(CitiesOpts(country, element?.code ?? ""));
  };

  const submitHandler = async () => {
    const profile: User = profileObj(userId ? userFound! : user!, watch());

    try {
      await updateUserDto(profile);
      if (!userId) updateUser(profile);
      Message().success(`The update SEABC Profile was successfully`);
    } catch (error) {
      Message().error(`The update SEABC Profile was not successfully`);
    }
  };

  const goToStudents = () => navigate("/SEABC/students");
  const getUserInfo = async () => setUserFound(await getUserDetails(userId!));
  const options: CheckboxGroupProps<string>["options"] = [
    { label: "Mr.", value: "mr" },
    { label: "Ms.", value: "ms" },
    { label: "Dr.", value: "dr" },
  ];
  useEffect(() => {
    if (userId) getUserInfo();
  }, [userId]);

  useEffect(() => {
    if (user) {
      setCountry(user.country);
      setState(user.province);
      const countryObj: ListsI[] = countries.filter((item) => {
        return item.value === user.country;
      });
      setCountryCode(countryObj[0]);
      setStates(StatesOpts(countryObj[0]?.code));
    }
  }, [user]);

  useEffect(() => {
    if (states.length && countryCode) {
      const provinceObj: ListsI[] = states.filter((item) => {
        return item.value === user?.province;
      });
      setCities(CitiesOpts(countryCode.code, provinceObj[0]?.code));
    }
  }, [states]);

  return (
    <>
      <div className="flex justify-center">
        {userId ? (
          <Button
            type="primary"
            style={{ marginTop: "1rem" }}
            icon={<UserOutlined />}
            onClick={() => goToStudents()}
          >
            All Students
          </Button>
        ) : (
          user?.role === "admin" && (
            <CBHandMessage color={"yellow"} extended>
              {'This will be the "general enquiries" contact info'}
            </CBHandMessage>
          )
        )}
      </div>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="flex gap-8 max-w-6xl pt-4" style={{ margin: "0 auto" }}>
          <CustomCard title="Contact Info">
            <div className="flex flex-col p-4">
              <CBInput
                className="flex-1"
                control={control}
                type="email"
                label="Email"
                name="email"
                required
                disabled
              />
              <CBInput
                className="flex-1"
                control={control}
                type="email"
                label="Contact Email"
                name="contactEmail"
                required
              />
              <div
                style={{
                  maxWidth: "23rem",
                  fontWeight: 400,
                  color: "rgba(0, 0, 0, 0.25)",
                }}
              >
                Your "Contact Email" Will be used by the administrator of the
                course to contact you if necessary.
              </div>
              <CBInput
                className="flex-1"
                control={control}
                type="phone"
                label="Phone"
                name="phone"
                required
              />
            </div>
          </CustomCard>
          <CustomCard title="Personal Info">
            <div className="p-5">
              <div className="flex gap-5 pt-5 pb-2">
                <Text>Title:</Text>
                <Radio.Group
                  onChange={onChangeTitle}
                  value={watch("title")}
                  className="flex gap-5"
                  options={options}
                />
              </div>
              <div className="flex gap-8">
                <CBSelect
                  className="flex-none w-32"
                  control={control}
                  label="Designation:"
                  name="designation"
                  required
                  defaultValue={"No"}
                  options={designationsOptions}
                />
                <CBInput
                  className="flex-1"
                  control={control}
                  type="text"
                  label="Last Name:"
                  name="lastName"
                  required
                />
                <CBInput
                  className="flex-1"
                  control={control}
                  type="text"
                  label="First Name: "
                  name="firstName"
                  required
                />
              </div>
              <div className="flex gap-8">
                <CBSelect
                  className="flex-none w-32"
                  control={control}
                  label="SEABC Member:"
                  name="member"
                  required
                  defaultValue={"No"}
                  options={[
                    { value: "1", label: "Yes" },
                    { value: "0", label: "No" },
                  ]}
                />
                {watch("member") === "1" && (
                  <CBInput
                    control={control}
                    label="Membership number:"
                    name="membership"
                    required
                  />
                )}
              </div>
              <div className="flex gap-6">
                <CBInput
                  className="flex-none w-24"
                  control={control}
                  type="text"
                  label="Postal Code:"
                  name="zipCode"
                  required
                />
                <CBSelect
                  className="flex-none w-44"
                  searchable={true}
                  control={control}
                  label="Country:"
                  name="country"
                  required
                  defaultValue={"No"}
                  options={countries}
                  onChange={changeCountry}
                />
                {states.length ? (
                  <CBSelect
                    searchable={true}
                    className="flex-none w-40"
                    control={control}
                    label="Province:"
                    name="province"
                    required
                    defaultValue={"No"}
                    options={states}
                    onChange={changeState}
                    disabled={country !== "" && country !== "0" ? false : true}
                  />
                ) : (
                  <CBInput
                    className="flex-1 w-40"
                    control={control}
                    type="text"
                    label="Province"
                    name="province"
                    disabled={country !== "0" ? false : true}
                  />
                )}
                {cities.length ? (
                  <CBSelect
                    searchable={true}
                    control={control}
                    label="City:"
                    name="city"
                    required
                    className="flex-none w-40"
                    defaultValue={"No"}
                    options={cities}
                    disabled={
                      country !== "" && country !== "0" && state !== ""
                        ? false
                        : true
                    }
                  />
                ) : (
                  <CBInput
                    className="flex-1 w-40"
                    control={control}
                    type="text"
                    label="City"
                    name="city"
                    required
                    disabled={
                      country !== "0" && (state !== "" || states.length === 0)
                        ? false
                        : true
                    }
                  />
                )}
              </div>
              <div className="flex gap-8">
                <CBInput
                  className="flex-none w-32"
                  control={control}
                  type="text"
                  label="Apt./Suite:"
                  name="aptSuite"
                />
                <CBInput
                  className="flex-1"
                  control={control}
                  type="text"
                  label="Address"
                  name="address"
                  required
                />
              </div>
            </div>
          </CustomCard>
        </div>
        <div className="flex justify-center">
          <Button
            type="primary"
            htmlType="submit"
            icon={<CheckOutlined />}
            className="w-48"
          >
            Save
          </Button>
        </div>
      </form>
    </>
  );
};

export default Profile;
